import { Box, SxProps, Theme, useMediaQuery } from '@mui/material';
import React from 'react';

import {
  CAROUSEL_PROGRESS_THUMB_MIN_PERCENT,
  CAROUSEL_PROGRESS_TRACK_HEIGHT,
  CAROUSEL_PROGRESS_TRACK_WIDTH,
  CAROUSEL_PROGRESS_TRANSITION_MS,
  getCarouselProgressLabel,
  PILL_BORDER_RADIUS,
  REDUCED_MOTION_QUERY,
} from '../../../constants/ui';

interface IProps {
  steps: number;
  activeStep: number;
}

const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const THUMB_TRANSITION = ['width', 'transform']
  .map((property) => `${property} ${CAROUSEL_PROGRESS_TRANSITION_MS}ms ${EASING}`)
  .join(', ');

const containerSx: SxProps<Theme> = {
  height: 28,
  mt: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
};

const trackSx: SxProps<Theme> = {
  position: 'relative',
  width: CAROUSEL_PROGRESS_TRACK_WIDTH,
  height: CAROUSEL_PROGRESS_TRACK_HEIGHT,
  borderRadius: PILL_BORDER_RADIUS,
  bgcolor: 'divider',
  overflow: 'hidden',
  flexShrink: 0,
};

const thumbSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  borderRadius: PILL_BORDER_RADIUS,
  bgcolor: 'primary.main',
};

const counterSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  letterSpacing: '0.2px',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};

export const CarouselProgress = ({ steps, activeStep }: IProps) => {
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  if (steps <= 1) {
    return null;
  }

  const thumbWidthPercent = Math.max(100 / steps, CAROUSEL_PROGRESS_THUMB_MIN_PERCENT);
  const freeTrackWidth = CAROUSEL_PROGRESS_TRACK_WIDTH * (1 - thumbWidthPercent / 100);
  const thumbOffset = Math.round((activeStep / (steps - 1)) * freeTrackWidth * 100) / 100;

  const thumbStyle: React.CSSProperties = {
    width: `${thumbWidthPercent}%`,
    transform: `translateX(${thumbOffset}px)`,
    transition: reduceMotion ? 'none' : THUMB_TRANSITION,
  };

  return (
    <Box sx={containerSx}>
      <Box role="presentation" sx={trackSx}>
        <Box component="span" style={thumbStyle} sx={thumbSx} />
      </Box>
      <Box component="span" aria-live="polite" aria-atomic="true" sx={counterSx}>
        {getCarouselProgressLabel(activeStep + 1, steps)}
      </Box>
    </Box>
  );
};
